import { useState, useEffect } from "react";
import { useProfileForm } from "../Hooks/useProfileForm";
import { Modal, Box, TextField } from "@mui/material";
import avatar from "../Assets/icons/avatar.png";
import { HiOutlineMail } from "react-icons/hi";
import { RiLockPasswordLine } from "react-icons/ri";
import { BsFillTelephoneFill } from "react-icons/bs";
import { IoMdSave } from "react-icons/io";
import {
  MdDevices,
  MdPhoneIphone,
  MdAndroid,
  MdDesktopWindows,
  MdLaptop,
  MdComputer,
  MdSmartphone,
} from "react-icons/md";
import { FiShield, FiEdit2 } from "react-icons/fi";
import { useAlert } from "../context/AlertContext";
import {
  useGetTrustedDevicesQuery,
  useUpdateTrustedDeviceLabelMutation,
  useRevokeTrustedDeviceMutation,
  useSend2faChangeEmailCodeMutation,
  useVerify2faChangeByEmailCodeMutation,
  useConfirm2faChangeMutation,
} from "../store";
import { useSendPasswordChangeEmailCodeMutation } from "../store/authApi";
import { formatDisplayIp, getEmailSendErrorMessage } from "../store/utils";
import { TelInput } from "../Components/atoms";
import EmailChangeModal from "../Components/Profile/EmailChangeModal";

// panel-profile 1.0 telefon kuralı (sunucu da aynısını uygular): boş = sil; dolu ise
// yalnız +, rakam, boşluk; 10–15 rakam.
const telError = (tel) => {
  const t = String(tel || "").trim();
  if (!t) return "";
  const digits = t.replace(/\D/g, "");
  if (!/^[+\d\s]+$/.test(t) || digits.length < 10 || digits.length > 15 || t.length > 20) {
    return "Geçerli bir telefon numarası girin.";
  }
  return "";
};

const profileStyles = {
  card: "bg-white rounded-xl border border-slate-200 shadow-sm",
  cardHeader: "text-slate-800 font-semibold",
  iconBox: "w-10 h-10 rounded-lg flex items-center justify-center bg-indigo-50 text-indigo-600",
  iconBoxSm: "w-9 h-9 rounded-lg flex items-center justify-center bg-indigo-50 text-indigo-600",
  btnPrimary: "inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium bg-indigo-600 text-white shadow-sm hover:bg-indigo-700 transition-colors",
  btnSecondary: "inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium border-2 border-indigo-600 text-indigo-600 bg-white hover:bg-indigo-600 hover:text-white transition-colors",
  label: "text-xs font-medium uppercase tracking-wider text-slate-500",
  value: "text-slate-800",
};

function formatDate(d) {
  if (!d) return "—";
  const date = new Date(d);
  return date.toLocaleDateString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Rol kodu → Türkçe etiket (profil sayfasında gösterim) */
const getRoleLabel = (role, subRole) => {
  const labels = {
    admin: "Sistem Yöneticisi",
    developer: "Geliştirici",
    management: "Yönetim",
    dealer: "Bayi",
    playmaker: "Plasiyer",
    service: "Servis",
    accounting: "Muhasebe",
    depo: "Depo", // bayi (dealer) subRole'ü
    manufacturer: "Üretim",
    customer: "Müşteri",
    enduser: "Son Kullanıcı",
    machineworker: "Makine İşçisi",
    storage: "Depo",
  };
  return labels[subRole] || labels[role] || role || "—";
};

/** Cihaz adı ve tipine göre ikon + kısa etiket döner (masaüstü/mobil/Windows/macOS vb.) */
function getDeviceIcon(deviceName = "", deviceType) {
  const name = (deviceName || "").toLowerCase();
  if (name.includes("ios") || name.includes("ipad") || name.includes("iphone")) {
    return { Icon: MdPhoneIphone, label: "iPhone / iPad", title: "iOS cihaz" };
  }
  if (name.includes("android")) {
    return { Icon: MdAndroid, label: "Android", title: "Android cihaz" };
  }
  if (name.includes("windows")) {
    return { Icon: MdDesktopWindows, label: "Windows", title: "Masaüstü (Windows)" };
  }
  if (name.includes("mac") || name.includes("macos")) {
    return { Icon: MdLaptop, label: "Mac", title: "Mac / macOS" };
  }
  if (name.includes("linux")) {
    return { Icon: MdComputer, label: "Linux", title: "Masaüstü (Linux)" };
  }
  if (deviceType === "mobile") {
    return { Icon: MdSmartphone, label: "Mobil", title: "Mobil cihaz" };
  }
  return { Icon: MdComputer, label: "Web", title: "Masaüstü / Web" };
}

export default function ProfilePage() {
  const {
    updateMe,
    updateMyPassword,
    userInfo,
    input,
    setInput,
    token,
    auth,
    resultUpdateMe,
    resultUpdateMyPassword,
  } = useProfileForm();
  const { showSuccess, showWarning, showError } = useAlert();

  const { data: devicesData, isLoading: devicesLoading } =
    useGetTrustedDevicesQuery(undefined, { skip: !token });
  const [updateDeviceLabel, { isLoading: updateLabelLoading }] =
    useUpdateTrustedDeviceLabelMutation();
  const [revokeDevice, { isLoading: revokeLoading }] =
    useRevokeTrustedDeviceMutation();
  const [editingDeviceId, setEditingDeviceId] = useState(null);
  const [editingLabel, setEditingLabel] = useState("");
  const [send2faChangeEmailCode, { data: send2faChangeCodeData, isLoading: sendEmailCodeLoading, isSuccess: sendEmailCodeSuccess, error: sendEmailCodeError }] =
    useSend2faChangeEmailCodeMutation();
  const [verify2faChangeByEmailCode, { data: changeSetupData, isLoading: changeStartLoading, error: changeStartError }] =
    useVerify2faChangeByEmailCodeMutation();
  const [confirm2faChange, { isLoading: changeConfirmLoading, isSuccess: changeConfirmSuccess, error: changeConfirmError }] =
    useConfirm2faChangeMutation();
  const [sendPasswordChangeEmailCode, { data: sendPasswordCodeData, isLoading: sendPasswordCodeLoading, isSuccess: sendPasswordCodeSuccess, error: sendPasswordCodeError }] =
    useSendPasswordChangeEmailCodeMutation();

  const [openProfileModal, setOpenProfileModal] = useState(false);
  const [openEmailModal, setOpenEmailModal] = useState(false);
  const [openPasswordModal, setOpenPasswordModal] = useState(false);
  const [passwordChangeStep, setPasswordChangeStep] = useState(1); // 1: yöntem seç, 2: 2FA formu veya e-posta kod gönder, 3: e-posta kodu + şifre (sadece e-posta seçildiyse)
  const [passwordChangeMethod, setPasswordChangeMethod] = useState(null); // "2fa" | "email"
  const [open2faChangeModal, setOpen2faChangeModal] = useState(false);
  const [twoFAChangeStep, setTwoFAChangeStep] = useState(1); // 1: e-posta kodu iste, 2: e-posta kodu gir, 3: yeni QR + kod onay
  const [twoFACode, setTwoFACode] = useState("");
  const [last2faChangeCodeInfo, setLast2faChangeCodeInfo] = useState(null); // { email, operationId }
  const [lastPasswordCodeSentInfo, setLastPasswordCodeSentInfo] = useState(null); // { email, operationId }

  const twoFactorEnabled = devicesData?.data?.twoFactorEnabled === true;
  const secondaryVerificationMethod = devicesData?.data?.secondaryVerificationMethod;
  const emailVerificationDone = secondaryVerificationMethod === "email" || secondaryVerificationMethod === "sms";

  const handleOpenProfileModal = () => {
    setInput((prev) => ({
      ...prev,
      name: userInfo.name,
      lastName: userInfo.lastName,
      email: userInfo.email,
      tel: userInfo.tel,
    }));
    setOpenProfileModal(true);
  };

  // MuiTelInput yalnız ülke kodu bıraktığında ("+90") telefon boş sayılır.
  const telValue = (() => {
    const t = String(input.tel || "").trim();
    return t.replace(/\D/g, "").length <= 3 && !/\d{4}/.test(t) ? "" : t;
  })();
  const profileTelError = telError(telValue);

  const handleProfileSubmit = (e) => {
    e.preventDefault();
    if (profileTelError) return;
    updateMe({
      name: input.name,
      lastName: input.lastName,
      tel: telValue,
      token: token,
    });
    setOpenProfileModal(false);
  };

  const handleOpenPasswordModal = () => {
    setInput((prev) => ({
      ...prev,
      passwordCurrent: "",
      password: "",
      passwordConfirm: "",
      emailCode: "",
      twoFactorCode: "",
    }));
    setPasswordChangeStep(1);
    setPasswordChangeMethod(null);
    setLastPasswordCodeSentInfo(null);
    setOpenPasswordModal(true);
  };

  const handlePasswordChangeChoose2FA = () => {
    setPasswordChangeMethod("2fa");
    setPasswordChangeStep(2);
  };

  const handlePasswordChangeChooseEmail = () => {
    setPasswordChangeMethod("email");
    setPasswordChangeStep(2);
  };

  const handleSendPasswordChangeCode = () => {
    sendPasswordChangeEmailCode(token);
  };

  useEffect(() => {
    if (sendPasswordCodeSuccess && passwordChangeMethod === "email") {
      const d = sendPasswordCodeData;
      setLastPasswordCodeSentInfo(
        d?.email != null || d?.operationId != null ? { email: d.email ?? "", operationId: d.operationId ?? "" } : null
      );
      setPasswordChangeStep(3);
    }
  }, [sendPasswordCodeSuccess, passwordChangeMethod, sendPasswordCodeData]);

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    const emailCode = (input.emailCode || "").trim();
    const twoFactorCode = (input.twoFactorCode || "").trim();
    if (passwordChangeMethod === "email" && emailCode.length !== 6) {
      showError("E-posta doğrulama kodu 6 haneli olmalıdır.");
      return;
    }
    if (passwordChangeMethod === "2fa" && twoFactorCode.length !== 6) {
      showError("2FA doğrulama kodu 6 haneli olmalıdır.");
      return;
    }
    if (!input.passwordCurrent?.trim() || !input.password?.trim() || !input.passwordConfirm?.trim()) return;
    if (input.password !== input.passwordConfirm) {
      showError("Yeni şifre ve tekrarı eşleşmiyor.");
      return;
    }
    if (input.password.length < 6) {
      showError("Yeni şifre en az 6 karakter olmalıdır.");
      return;
    }
    updateMyPassword({
      ...(passwordChangeMethod === "email" ? { emailCode } : { twoFactorCode: twoFactorCode.trim() }),
      passwordCurrent: input.passwordCurrent,
      password: input.password,
      passwordConfirm: input.passwordConfirm,
      token: token,
    });
  };

  useEffect(() => {
    if (resultUpdateMyPassword.isSuccess) {
      setOpenPasswordModal(false);
      setPasswordChangeStep(1);
      setPasswordChangeMethod(null);
      setInput((prev) => ({ ...prev, passwordCurrent: "", password: "", passwordConfirm: "", emailCode: "", twoFactorCode: "" }));
    }
  }, [resultUpdateMyPassword.isSuccess, setInput]);

  const handleOpen2faChangeModal = () => {
    setOpen2faChangeModal(true);
    setTwoFAChangeStep(1);
    setTwoFACode("");
  };

  const handleClose2faChangeModal = () => {
    setOpen2faChangeModal(false);
    setTwoFAChangeStep(1);
    setTwoFACode("");
    setLast2faChangeCodeInfo(null);
  };

  const handleSend2faChangeEmailCode = () => {
    setLast2faChangeCodeInfo(null);
    send2faChangeEmailCode(token);
  };

  useEffect(() => {
    if (sendEmailCodeSuccess) {
      const d = send2faChangeCodeData;
      setLast2faChangeCodeInfo(
        d?.email != null || d?.operationId != null ? { email: d.email ?? "", operationId: d.operationId ?? "" } : null
      );
      setTwoFAChangeStep(2);
      setTwoFACode("");
    }
  }, [sendEmailCodeSuccess, send2faChangeCodeData]);

  const handleVerify2faChangeByEmailCode = (e) => {
    e.preventDefault();
    if (!twoFACode.trim() || twoFACode.length !== 6) return;
    verify2faChangeByEmailCode({ code: twoFACode.trim(), token }).then((res) => {
      if (!res.error) {
        setTwoFAChangeStep(3);
        setTwoFACode("");
      }
    });
  };

  const handleConfirm2faChange = (e) => {
    e.preventDefault();
    if (!twoFACode.trim() || twoFACode.length !== 6) return;
    confirm2faChange({ code: twoFACode.trim(), token }).then((res) => {
      if (!res.error) {
        handleClose2faChangeModal();
      }
    });
  };

  const sendEmailCodeErrorMessage = sendEmailCodeError ? getEmailSendErrorMessage(sendEmailCodeError) : null;
  const changeStartErrorMessage = changeStartError?.data?.message || changeStartError?.message;
  const changeConfirmErrorMessage =
    changeConfirmError?.data?.code === "invalid_2fa_code"
      ? "Geçersiz kod. Yeni cihazdaki (Google Authenticator) 6 haneli kodu girin."
      : changeConfirmError?.data?.message || changeConfirmError?.message;

  useEffect(() => {
    if (changeConfirmSuccess) {
      showSuccess("2FA cihazı güncellendi. Yeni telefonunuzdaki Google Authenticator ile giriş yapabilirsiniz.");
    }
  }, [changeConfirmSuccess, showSuccess]);

  return (
    <div className="min-h-screen">
      <div className="w-full px-4 py-6 sm:px-6 lg:px-8">
        {/* İki sütun: eşit yükseklik, yatayda sayfa genişliği, responsive */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 items-stretch">
        {/* Sol sütun: Profil bilgisi — kart sütun yüksekliğini doldurur */}
        <div className={`${profileStyles.card} overflow-hidden flex flex-col h-full min-h-0`}>
          <div className="bg-slate-50 border-b border-slate-200 px-6 py-6 shrink-0">
            <div className="flex items-center gap-5">
              <div className="relative">
                <img
                  src={avatar}
                  alt="Profil"
                  className="w-20 h-20 rounded-xl object-cover ring-2 ring-white shadow"
                />
                <span className="absolute -bottom-1 -right-1 w-5 h-5 bg-emerald-500 rounded-full border-2 border-white" title="Aktif" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-xl font-semibold text-slate-800 tracking-tight">
                  {userInfo.name} {userInfo.lastName}
                </h2>
                <p className="text-sm text-slate-600 mt-0.5">{userInfo.email}</p>
              </div>
            </div>
          </div>

          <div className="px-6 py-5 space-y-0 flex-1 min-h-0">
            <div className="flex items-center gap-4 py-3 border-b border-slate-100">
              <div className={profileStyles.iconBox}>
                <BsFillTelephoneFill className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className={profileStyles.label}>Telefon</p>
                <p className={profileStyles.value}>{userInfo.tel || "—"}</p>
              </div>
            </div>
            <div className="flex items-center gap-4 py-3 border-b border-slate-100">
              <div className={profileStyles.iconBox}>
                <HiOutlineMail className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className={profileStyles.label}>E-posta</p>
                <p className={profileStyles.value}>{userInfo.email}</p>
              </div>
              <button
                type="button"
                onClick={() => setOpenEmailModal(true)}
                className="shrink-0 inline-flex items-center gap-1.5 text-sm font-Medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 px-3 py-1.5 rounded-lg transition-colors"
              >
                <FiEdit2 className="w-3.5 h-3.5" />
                Değiştir
              </button>
            </div>
            <div className="flex items-center gap-4 py-3 border-b border-slate-100">
              <div className={profileStyles.iconBox}>
                <FiShield className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className={profileStyles.label}>Rol</p>
                <p className={profileStyles.value}>{getRoleLabel(auth?.role, auth?.subRole)}</p>
              </div>
            </div>
            <div className="flex items-center gap-4 py-3">
              <div className={profileStyles.iconBox}>
                <RiLockPasswordLine className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className={profileStyles.label}>Şifre</p>
                <p className={profileStyles.value}>********</p>
              </div>
            </div>
          </div>

          <div className="px-6 pb-6 flex flex-wrap gap-3 shrink-0 mt-auto">
            <button onClick={handleOpenProfileModal} className={profileStyles.btnPrimary}>
              <IoMdSave className="w-5 h-5" />
              Profili Düzenle
            </button>
            <button onClick={handleOpenPasswordModal} className={profileStyles.btnSecondary}>
              <RiLockPasswordLine className="w-5 h-5" />
              Şifre Değiştir
            </button>
          </div>
        </div>

        {/* Sağ sütun: Cihazlar + Güvenlik — sütun yüksekliğini doldurur, eşit yükseklik */}
        <div className="flex flex-col gap-4 sm:gap-6 h-full min-h-0">
        {/* Giriş yapılan cihazlar kartı */}
        <div className={`${profileStyles.card} p-6 shrink-0`}>
          <h3 className="text-base font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <div className={profileStyles.iconBoxSm}>
              <MdDevices className="w-5 h-5" />
            </div>
            Giriş yapılan cihazlar
          </h3>
          {devicesLoading ? (
            <p className="text-sm text-slate-600">Yükleniyor...</p>
          ) : devicesData?.data?.devices?.length > 0 ? (
            <ul className="space-y-0">
              {devicesData.data.devices.map((dev) => {
                const displayName = dev.label || dev.deviceName || "Bilinmeyen cihaz";
                const { Icon, label: deviceTypeLabel, title } = getDeviceIcon(dev.deviceName, dev.deviceType);
                const isEditing = editingDeviceId === dev._id;
                return (
                <li
                  key={dev._id}
                  className="flex flex-wrap items-center justify-between gap-3 py-4 border-b border-slate-100 last:border-0"
                >
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div
                      className={`${profileStyles.iconBoxSm} shrink-0`}
                      title={title}
                    >
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      {isEditing ? (
                        <div className="flex items-center gap-2 flex-wrap">
                          <input
                            type="text"
                            value={editingLabel}
                            onChange={(e) => setEditingLabel(e.target.value.slice(0, 100))}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                updateDeviceLabel({ id: dev._id, label: editingLabel.trim() }).then(() => setEditingDeviceId(null));
                              }
                              if (e.key === "Escape") setEditingDeviceId(null);
                            }}
                            onBlur={() => {
                              updateDeviceLabel({ id: dev._id, label: editingLabel.trim() }).then(() => setEditingDeviceId(null));
                            }}
                            placeholder="Cihaz adı (isteğe bağlı)"
                            className="flex-1 min-w-[140px] px-2 py-1 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            autoFocus
                          />
                          <span className="text-xs text-slate-500">En fazla 100 karakter</span>
                        </div>
                      ) : (
                        <p className="font-medium text-slate-800">
                          {displayName}
                          {dev.isCurrentDevice && (
                            <span className="ml-2 text-xs font-medium bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-md">
                              Bu cihaz
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              setEditingDeviceId(dev._id);
                              setEditingLabel(dev.label ?? "");
                            }}
                            className="ml-2 inline-flex items-center text-slate-400 hover:text-indigo-600 transition-colors"
                            title="Özel isim ver / düzenle"
                          >
                            <FiEdit2 className="w-4 h-4" />
                          </button>
                        </p>
                      )}
                      <p className="text-sm text-slate-600 mt-0.5">
                        <span className="font-medium text-slate-500">{deviceTypeLabel}</span>
                        {" • "}
                        Son giriş: {formatDate(dev.lastUsedAt)}
                        {dev.lastIp ? ` • IP: ${formatDisplayIp(dev.lastIp)}` : ""}
                      </p>
                    </div>
                  </div>
                  {!dev.isCurrentDevice && !isEditing && (
                    <button
                      type="button"
                      disabled={revokeLoading}
                      onClick={() => revokeDevice(dev._id)}
                      className="text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
                    >
                      Cihazı kaldır
                    </button>
                  )}
                </li>
              );
              })}
            </ul>
          ) : (
            <p className="text-sm text-slate-600">
              Henüz güvenilen cihaz kaydı yok. 2FA açıksa, giriş yaptığınız cihazlar burada listelenir (en fazla 4).
            </p>
          )}
        </div>

        {/* Güvenlik doğrulamaları — kalan yüksekliği doldurur */}
        <div className={`${profileStyles.card} p-6 flex-1 min-h-0 flex flex-col`}>
          <h3 className="text-base font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <div className={profileStyles.iconBoxSm}>
              <FiShield className="w-5 h-5" />
            </div>
            Güvenlik doğrulamaları
          </h3>
          <ul className="space-y-3">
            <li className="flex flex-wrap items-center justify-between gap-2 py-2 border-b border-slate-100 last:border-0">
              <span className="text-slate-700">İki adımlı doğrulama (2FA)</span>
              <div className="flex items-center gap-2">
                {twoFactorEnabled ? (
                  <span className="text-xs font-medium bg-emerald-100 text-emerald-700 px-2 py-1 rounded-md">Yapıldı</span>
                ) : (
                  <span className="text-xs font-medium bg-amber-100 text-amber-700 px-2 py-1 rounded-md">Yapılmadı</span>
                )}
                {twoFactorEnabled && (
                  <button
                    type="button"
                    onClick={handleOpen2faChangeModal}
                    className="text-sm font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 px-2 py-1 rounded-lg transition-colors"
                  >
                    2FA cihazını değiştir
                  </button>
                )}
              </div>
            </li>
            <li className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
              <span className="text-slate-700">E-posta doğrulama</span>
              {emailVerificationDone ? (
                <span className="text-xs font-medium bg-emerald-100 text-emerald-700 px-2 py-1 rounded-md">
                  {secondaryVerificationMethod === "sms" ? "SMS ile yapıldı" : "Yapıldı"}
                </span>
              ) : (
                <span className="text-xs font-medium bg-amber-100 text-amber-700 px-2 py-1 rounded-md">Yapılmadı</span>
              )}
            </li>
          </ul>
          <p className="text-xs text-slate-500 mt-3 shrink-0">
            Girişte 2FA ve e-posta doğrulaması tamamlanmış olmalıdır. Eksikse bir sonraki girişte sırayla istenecektir.
          </p>
        </div>
        </div>
        </div>
      </div>

      <EmailChangeModal
        open={openEmailModal}
        onClose={() => setOpenEmailModal(false)}
        currentEmail={userInfo.email}
      />

      {/* Profil düzenleme modal */}
      <Modal open={openProfileModal} onClose={() => setOpenProfileModal(false)}>
        <Box className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-xl border border-slate-200 p-6 w-full max-w-md">
          <h2 className="text-xl font-semibold text-slate-800 mb-6">Profil bilgilerini güncelle</h2>
          <form onSubmit={handleProfileSubmit} className="space-y-4">
            <TextField fullWidth label="İsim" value={input.name} onChange={(e) => setInput({ ...input, name: e.target.value })} />
            <TextField fullWidth label="Soyisim" value={input.lastName} onChange={(e) => setInput({ ...input, lastName: e.target.value })} />
            <TelInput
              label="Telefon"
              value={input.tel || ""}
              onChange={(e) => setInput({ ...input, tel: e.target.value })}
              error={Boolean(profileTelError)}
              errorMessage={profileTelError}
            />
            <p className="text-xs text-slate-500 -mt-2">E-postayı değiştirmek için profil kartındaki E-posta satırında “Değiştir”i kullanın.</p>
            <button type="submit" className="w-full py-2.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors font-medium">
              Kaydet
            </button>
          </form>
        </Box>
      </Modal>

      {/* Şifre değiştirme modal — 1) Yöntem seç (2FA veya E-posta), 2) Doğrulama + şifre */}
      <Modal open={openPasswordModal} onClose={() => setOpenPasswordModal(false)}>
        <Box className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-xl border border-slate-200 p-6 w-full max-w-md">
          <h2 className="text-xl font-semibold text-slate-800 mb-4">Şifre değiştir</h2>

          {/* Adım 1: 2FA veya E-posta seç */}
          {passwordChangeStep === 1 && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Şifre değiştirmek için önce kimliğinizi doğrulamanız gerekir. İki adımlı doğrulama (2FA) kullanıyorsanız telefonunuzdaki kodu girebilir veya e-posta adresinize gönderilecek kodu kullanabilirsiniz.
              </p>
              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={handlePasswordChangeChoose2FA}
                  className="w-full py-3 px-4 rounded-xl border-2 border-indigo-600 text-indigo-600 bg-white hover:bg-indigo-50 font-medium transition-colors text-left"
                >
                  <span className="font-semibold">2FA ile doğrula</span>
                  <span className="block text-xs text-slate-500 mt-0.5">Telefonunuzdaki uygulama (Google Authenticator) kodunu girin</span>
                </button>
                <button
                  type="button"
                  onClick={handlePasswordChangeChooseEmail}
                  className="w-full py-3 px-4 rounded-xl border-2 border-indigo-600 text-indigo-600 bg-white hover:bg-indigo-50 font-medium transition-colors text-left"
                >
                  <span className="font-semibold">E-posta ile doğrula</span>
                  <span className="block text-xs text-slate-500 mt-0.5">E-postanıza gönderilecek 6 haneli kodu kullanın</span>
                </button>
              </div>
              <button
                type="button"
                onClick={() => setOpenPasswordModal(false)}
                className="w-full py-2.5 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium transition-colors"
              >
                İptal
              </button>
            </div>
          )}

          {/* Adım 2 (E-posta): E-postaya kod gönder */}
          {passwordChangeStep === 2 && passwordChangeMethod === "email" && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                E-posta adresinize 6 haneli bir doğrulama kodu göndereceğiz. &quot;E-postaya kod gönder&quot;e tıklayın.
              </p>
              {sendPasswordCodeError && (
                <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
                  {getEmailSendErrorMessage(sendPasswordCodeError)}
                </p>
              )}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => { setPasswordChangeStep(1); setPasswordChangeMethod(null); }}
                  className="flex-1 py-2.5 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium transition-colors"
                >
                  Geri
                </button>
                <button
                  type="button"
                  onClick={handleSendPasswordChangeCode}
                  disabled={sendPasswordCodeLoading}
                  className="flex-1 py-2.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 font-medium disabled:opacity-50"
                >
                  {sendPasswordCodeLoading ? "Gönderiliyor..." : "E-postaya kod gönder"}
                </button>
              </div>
            </div>
          )}

          {/* Adım 2 (2FA) veya Adım 3 (E-posta): Doğrulama kodu + şifre formu */}
          {(passwordChangeStep === 2 && passwordChangeMethod === "2fa") || (passwordChangeStep === 3 && passwordChangeMethod === "email") ? (
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <p className="text-sm text-slate-600 mb-2">
                {passwordChangeMethod === "2fa"
                  ? "Telefonunuzdaki 2FA uygulamasından 6 haneli kodu ve şifre bilgilerinizi girin."
                  : "E-postanıza gelen 6 haneli kodu ve şifre bilgilerinizi girin."}
              </p>
              {resultUpdateMyPassword.isError && (
                <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
                  {resultUpdateMyPassword?.error?.data?.message || "Şifre güncellenemedi. Kodu ve mevcut şifrenizi kontrol edin."}
                </p>
              )}
              {passwordChangeMethod === "2fa" ? (
                <TextField
                  fullWidth
                  label="2FA doğrulama kodu (6 hane)"
                  placeholder="000000"
                  value={(input.twoFactorCode || "").slice(0, 6)}
                  onChange={(e) => setInput({ ...input, twoFactorCode: e.target.value.replace(/\D/g, "").slice(0, 6) })}
                  inputProps={{ maxLength: 6, inputMode: "numeric", pattern: "[0-9]*" }}
                />
              ) : (
                <>
                  {lastPasswordCodeSentInfo && (lastPasswordCodeSentInfo.email || lastPasswordCodeSentInfo.operationId) && (
                    <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-700">
                      {lastPasswordCodeSentInfo.email && <p>Kod <strong>{lastPasswordCodeSentInfo.email}</strong> adresine gönderildi.</p>}
                      {lastPasswordCodeSentInfo.operationId && <p className="mt-0.5">İşlem no: <strong className="font-mono">{lastPasswordCodeSentInfo.operationId}</strong></p>}
                    </div>
                  )}
                  <TextField
                    fullWidth
                    label="E-posta doğrulama kodu (6 hane)"
                    placeholder="000000"
                    value={(input.emailCode || "").slice(0, 6)}
                    onChange={(e) => setInput({ ...input, emailCode: e.target.value.replace(/\D/g, "").slice(0, 6) })}
                    inputProps={{ maxLength: 6, inputMode: "numeric", pattern: "[0-9]*" }}
                  />
                </>
              )}
              <TextField
                fullWidth
                type="password"
                label="Mevcut şifre"
                autoComplete="current-password"
                value={input.passwordCurrent || ""}
                onChange={(e) => setInput({ ...input, passwordCurrent: e.target.value })}
              />
              <TextField
                fullWidth
                type="password"
                label="Yeni şifre (en az 6 karakter)"
                autoComplete="new-password"
                value={input.password || ""}
                onChange={(e) => setInput({ ...input, password: e.target.value })}
              />
              <TextField
                fullWidth
                type="password"
                label="Yeni şifre tekrar"
                autoComplete="new-password"
                value={input.passwordConfirm || ""}
                onChange={(e) => setInput({ ...input, passwordConfirm: e.target.value })}
              />
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    if (passwordChangeMethod === "2fa") {
                      setPasswordChangeStep(1);
                      setPasswordChangeMethod(null);
                    } else {
                      setPasswordChangeStep(2);
                    }
                  }}
                  className="flex-1 py-2.5 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium transition-colors"
                >
                  Geri
                </button>
                <button
                  type="submit"
                  disabled={
                    resultUpdateMyPassword.isLoading ||
                    (passwordChangeMethod === "email" ? (input.emailCode || "").trim().length !== 6 : (input.twoFactorCode || "").trim().length !== 6) ||
                    !input.passwordCurrent?.trim() ||
                    !input.password?.trim() ||
                    !input.passwordConfirm?.trim()
                  }
                  className="flex-1 py-2.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors font-medium disabled:opacity-50"
                >
                  {resultUpdateMyPassword.isLoading ? "Güncelleniyor..." : "Şifreyi güncelle"}
                </button>
              </div>
            </form>
          ) : null}
        </Box>
      </Modal>

      {/* 2FA cihaz değiştir modal (e-posta ile doğrulama — 3 adım) */}
      <Modal open={open2faChangeModal} onClose={handleClose2faChangeModal}>
        <Box className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-xl border border-slate-200 p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
          <h2 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <FiShield className="w-6 h-6 text-indigo-600" />
            2FA cihazını değiştir
          </h2>
          {twoFAChangeStep === 1 && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Telefonu kaybettiyseniz veya yeni bir telefonda 2FA kullanacaksanız, e-posta adresinize gönderilecek doğrulama kodu ile işleme devam edebilirsiniz.
              </p>
              {sendEmailCodeErrorMessage && (
                <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{sendEmailCodeErrorMessage}</p>
              )}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleClose2faChangeModal}
                  className="flex-1 py-2.5 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium transition-colors"
                >
                  İptal
                </button>
                <button
                  type="button"
                  onClick={handleSend2faChangeEmailCode}
                  disabled={sendEmailCodeLoading}
                  className="flex-1 py-2.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 font-medium disabled:opacity-50 transition-colors"
                >
                  {sendEmailCodeLoading ? "Gönderiliyor..." : "E-postaya kod gönder"}
                </button>
              </div>
            </div>
          )}
          {twoFAChangeStep === 2 && (
            <form onSubmit={handleVerify2faChangeByEmailCode} className="space-y-4">
              <p className="text-sm text-gray-600">
                E-posta adresinize gelen 6 haneli doğrulama kodunu girin.
              </p>
              {last2faChangeCodeInfo && (last2faChangeCodeInfo.email || last2faChangeCodeInfo.operationId) && (
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-700">
                  {last2faChangeCodeInfo.email && <p>Kod <strong>{last2faChangeCodeInfo.email}</strong> adresine gönderildi.</p>}
                  {last2faChangeCodeInfo.operationId && <p className="mt-0.5">İşlem no: <strong className="font-mono">{last2faChangeCodeInfo.operationId}</strong></p>}
                </div>
              )}
              {changeStartErrorMessage && (
                <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{changeStartErrorMessage}</p>
              )}
              <TextField
                fullWidth
                label="E-posta doğrulama kodu"
                placeholder="000000"
                value={twoFACode}
                onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                inputProps={{ maxLength: 6, inputMode: "numeric", pattern: "[0-9]*" }}
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => { setTwoFAChangeStep(1); setTwoFACode(""); }}
                  className="flex-1 py-2.5 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium transition-colors"
                >
                  Geri
                </button>
                <button
                  type="submit"
                  disabled={twoFACode.length !== 6 || changeStartLoading}
                  className="flex-1 py-2.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 font-medium disabled:opacity-50 transition-colors"
                >
                  {changeStartLoading ? "Doğrulanıyor..." : "Doğrula"}
                </button>
              </div>
            </form>
          )}
          {twoFAChangeStep === 3 && changeSetupData?.data && (
            <form onSubmit={handleConfirm2faChange} className="space-y-4">
              <p className="text-sm text-gray-600">
                Yeni telefonunuzda Google Authenticator ile aşağıdaki QR kodu tarayın veya kodu girin, ardından oluşan 6 haneli kodu yazın.
              </p>
              {changeSetupData.data.qrCodeUrl && (
                <div className="flex justify-center p-4 bg-gray-50 rounded-xl">
                  <img src={changeSetupData.data.qrCodeUrl} alt="Yeni 2FA QR" className="w-40 h-40 object-contain" />
                </div>
              )}
              {changeSetupData.data.secretBase32 && (
                <p className="text-xs text-gray-500 break-all font-mono bg-gray-50 p-2 rounded">{changeSetupData.data.secretBase32}</p>
              )}
              {changeConfirmErrorMessage && (
                <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{changeConfirmErrorMessage}</p>
              )}
              <TextField
                fullWidth
                label="Yeni cihazdan 6 haneli kod"
                placeholder="000000"
                value={twoFACode}
                onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                inputProps={{ maxLength: 6, inputMode: "numeric", pattern: "[0-9]*" }}
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => { setTwoFAChangeStep(2); setTwoFACode(""); }}
                  className="flex-1 py-2.5 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium transition-colors"
                >
                  Geri
                </button>
                <button
                  type="submit"
                  disabled={twoFACode.length !== 6 || changeConfirmLoading}
                  className="flex-1 py-2.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 font-medium disabled:opacity-50 transition-colors"
                >
                  {changeConfirmLoading ? "Kaydediliyor..." : "Onayla"}
                </button>
              </div>
            </form>
          )}
        </Box>
      </Modal>
    </div>
  );
}
